import React from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Color, Flex, Icon, Link, Text } from "src/elements";
import { IconType } from "src/elements/Icon";
import { useTranslation } from "src/i18n";
import { Transition, pages } from "src/utils";
import styled from "styled-components";
import { sidebarMenu, SidebarMenuItem } from "./store/constants";
import { useMemo, useRef } from "react";
import { useUser } from "src/atoms/user";
import { hasPermission } from "@CommonUtils/RolesPermissions";
import { useHoverClickable } from "src/utils/hoverClickable";

export const sidebarWidth = 96;
export const sidebarPadding = 0;

const SidebarElement = styled(Flex)`
  position: fixed;
  flex-direction: column;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${sidebarWidth}px;
  background-color: ${Color.white};
  border-right: 1px solid ${Color.lightGrayHover};
  gap: 0;
  z-index: 10;
`;

const Menu = styled(Flex)`
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  min-height: 0;
  overflow-y: hidden;
  padding: 8px 0 0;
`;

const MenuItemContainer = styled(Flex)<{
  $bgColor: string;
}>`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 6px;
  transition: all ${Transition.fast};
  background: ${({ $bgColor }) => $bgColor};
`;

const LogoSection = styled.div`
  border-bottom: 1px solid ${Color.lightGrayHover};
`;

const BottomSection = styled(Flex)`
  flex-direction: column;
  border-top: 1px solid ${Color.lightGrayHover};
  padding: 2px 0;
`;

const ProfileAvatar = styled(Flex)`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: ${Color.primary};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const getMenuItemColors = (
  isActive: boolean,
  isHovered: boolean,
  highlightGreen?: boolean,
): { bg: string; iconColor: string; textColor: string } => {
  if (isActive && highlightGreen) {
    return { bg: Color.primary, iconColor: Color.white, textColor: Color.white };
  }
  if (isActive) {
    return { bg: "rgba(63, 142, 106, 0.1)", iconColor: Color.primary, textColor: Color.primary };
  }
  if (highlightGreen) {
    if (isHovered)
      return { bg: "rgba(63, 142, 106, 0.1)", iconColor: Color.primary, textColor: Color.primary };
    return { bg: Color.transparent, iconColor: Color.primary, textColor: Color.primary };
  }
  if (isHovered)
    return {
      bg: Color.lightGray,
      iconColor: Color.spaceGrayHover,
      textColor: Color.spaceGrayHover,
    };
  return { bg: Color.transparent, iconColor: Color.spaceGray, textColor: Color.spaceGray };
};

interface MenuItemProps {
  title?: string;
  path: string;
  icon?: IconType;
  iconNode?: React.ReactNode;
  size?: number;
  highlightGreen?: boolean;
}

const MenuItem = ({ title, path, icon, iconNode, size, highlightGreen }: MenuItemProps) => {
  const { pathname } = useLocation();

  const refMenuItem = useRef(null);
  const { isHovered } = useHoverClickable(refMenuItem);

  const isActive = !!matchPath({ path, end: false }, pathname);
  const colors = getMenuItemColors(isActive, isHovered, highlightGreen);

  return (
    <Link to={path} width="100%" minWidth="100%">
      <MenuItemContainer ref={refMenuItem} $bgColor={colors.bg}>
        {iconNode ??
          (icon && <Icon name={icon} color={colors.iconColor} width={size} height={size} />)}

        {title && (
          <Text variant="small2" color={colors.textColor} textAlign="center">
            {title}
          </Text>
        )}
      </MenuItemContainer>
    </Link>
  );
};

const ProfileMenuItem = ({
  user,
  title,
}: {
  user: ReturnType<typeof useUser>["user"];
  title: string;
}) => {
  const initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();

  return (
    <MenuItem
      path={pages.profile}
      title={title}
      iconNode={
        <ProfileAvatar>
          <Text variant="caption2" color={Color.white}>
            {initials}
          </Text>
        </ProfileAvatar>
      }
    />
  );
};

export const Sidebar = () => {
  const { user } = useUser();
  const { t } = useTranslation("common");

  const visibleMenu = useMemo(
    () =>
      sidebarMenu.filter(
        (item) =>
          !item.hidden &&
          (!item.permission || (user && hasPermission(user.roleCode, item.permission))),
      ),
    [user],
  );

  return (
    <SidebarElement>
      <LogoSection>
        <MenuItem path={pages.controlPanel} icon="logo" />
      </LogoSection>

      <Menu>
        {visibleMenu.map((item: SidebarMenuItem) => (
          <MenuItem
            key={item.path}
            size={20}
            path={item.path}
            icon={item.icon}
            highlightGreen={item.highlightGreen}
            title={t(`sidebar.${item.titleKey}` as const)}
          />
        ))}
      </Menu>

      <BottomSection>
        <ProfileMenuItem user={user} title={t("sidebar.profile")} />
        <MenuItem title={t("sidebar.settings")} path={pages.settings} icon="settings" size={18} />
        <MenuItem title={t("sidebar.logout")} path={pages.logout} icon="logout" size={18} />
      </BottomSection>
    </SidebarElement>
  );
};
